/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ModelTrainingStatus, TrainingState } from 'src/luis/types/trainingTypes';

export const modelTrainingStatusAnalyzer = {
    /**
     * Checks if all of the models are queued for training or not.
     *
     * @param models The models to check.
     * @returns True if all the models are queued and vice versa.
     */
    isQueued: (models: ModelTrainingStatus[]): boolean => models.every(m => m.statusId === 'queued'),

    /**
     * Checks if any of the models failed in training or not.
     *
     * @param models The models to check.
     * @returns The model that failed and undefined if not found.
     */
    getFailedModel: (models: ModelTrainingStatus[]): ModelTrainingStatus => models.find(m => m.statusId === 'fail'),

    /**
     * Checks if any of the models are still uploading.
     *
     * @param models The models to check.
     * @returns True if any of the models is still collecting data and vice versa.
     */
    isUploadingModel: (models: ModelTrainingStatus[]): boolean => models.some(m => m.progressSubStatus === 'uploadingModel'),

    /**
     * Checks if any of the models are still collecting data or not.
     *
     * @param models The models to check.
     * @returns True if any of the models is still collecting data and vice versa.
     */
    isCollectingData: (models: ModelTrainingStatus[]): boolean => models.every(m => m.progressSubStatus === 'collectingData'),

    /**
     * Checks if all of the models are up to date or not.
     *
     * @param models The models to check.
     * @returns True if all of the models are up to date and vice versa.
     */
    isUpToDate: (models: ModelTrainingStatus[]): boolean => models.every(m => m.statusId === 'upToDate'),

    /**
     * Checks if all of the models completed training or not.
     *
     * @param models The models to check.
     * @returns True if all of the models completed training and vice versa.
     */
    isCompleted: (models: ModelTrainingStatus[]): boolean => {
        const successStates: TrainingState[] = ['success', 'sampling', 'upToDate'];

        return models.filter(m => successStates.indexOf(m.statusId) !== -1).length === models.length;
    },

    /**
     * Gets the number of completed models.
     *
     * @param models The models to check.
     * @returns The number of completed models.
     */
    getCompletedModels: (models: ModelTrainingStatus[]): number =>
        models.filter(m => m.statusId === 'success' || m.statusId === 'upToDate').length
};
